<template>
  <div class="vx-col w-full mb-base">
    <vx-card ref="infoCard" :title="$t('CashFlow.Compilation.Edit')" title-color="black">
      <vs-row vs-type="flex" vs-justify="flex-end">
        <vs-col vs-type="flex" vs-justify="flex-end" vs-align="center" vs-w="12">
          <vs-button class="mr-3" v-if="canCalculate()" @click="calculate">{{ $t('CashFlow.Compilation.SaveAndCalculate') }}</vs-button>
          <vs-button class="mr-3" @click="submitData">{{ $t('Save') }}</vs-button>
          <vs-button type="border" color="danger" @click="returnToList">{{ $t('Cancel') }}</vs-button>
        </vs-col>
      </vs-row>
      <ValidationObserver ref="observer">
        <vs-tabs v-model="selectedTab">
          <vs-tab value="0" :label="$t('CashFlow.Compilation.Tab1')" icon-pack="feather" icon="icon-settings">
            <div class="p-6">
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <ValidationProvider :name="$t('CashFlow.Compilation.Name')" rules="required" v-slot="{ errors }">
                    <validated-input
                      :label="$t('CashFlow.Compilation.Name')"
                      v-model="compilationName"
                    ></validated-input>
                    <validation-message>{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <ValidationProvider :name="$t('CashFlow.Compilation.Description')" v-slot="{ errors }">
                    <validated-input
                      :label="$t('CashFlow.Compilation.Description')"
                      v-model="compilationDescription"
                    ></validated-input>
                    <validation-message>{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="12">
                  <form-label>{{$t('CashFlow.CorporateLom.Title')}}</form-label>
                  <ValidationProvider :name="$t('CashFlow.CorporateLom.Title')" v-slot="{ errors }">
                    <v-select
                      label="name"
                      :options="corporateLoms"
                      v-model="compilationCorporateLomId"
                      :reduce="corporateLom => corporateLom.id"
                      class="w-full"
                    />
                    <validation-message>{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="6" class="mb-5">
                  <form-label>{{$t('CashFlow.Compilation.Availability')}}</form-label>
                  <vs-row>
                    <vs-checkbox v-model="compilationIsPublic">{{$t('CashFlow.Lom.AvailabilityPublic')}}</vs-checkbox>
                  </vs-row>
                </vs-col>
                <vs-col vs-w="5">
                  <form-label>{{$t('CashFlow.Compilation.Availability')}}</form-label>
                  <vs-row>
                    <vs-checkbox v-model="compilationIsDecarbonizationScenario">{{$t('CashFlow.Compilation.IsDecarbonizationScenario')}}</vs-checkbox>
                  </vs-row>
                </vs-col>
              </vs-row>
            </div>
          </vs-tab>
          <vs-tab value="1" :label="$t('CashFlow.Compilation.Tab2')" icon-pack="feather" icon="icon-calendar">
            <div class="p-6">
              <vs-row class="mb-3">
                <vs-col vs-w="5" >
                  <vs-input
                    icon="search"
                    icon-after="true"  
                    class="mb-4 md:mb-0 mr-4"
                    v-model="searchQuery"
                    @input="updateSearchQuery"
                    placeholder="Search..."
                  />
                </vs-col>
                <vs-col vs-w="1">
                  <vs-switch class="mt-1" color="success" icon-pack="feather"  v-model="isExternalProject">
                    <template #off>
                      {{$t('CashFlow.ProjectLom.HyperionEntityName')}}
                    </template>
                    <template #on>
                      {{$t('CashFlow.ProjectLom.ExternalProjectName')}}
                    </template>
                  </vs-switch>
                </vs-col>          
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="6" class="mr-6">
                  <vs-list class="list-scrollable" v-show="isExternalProject" >
                    <vs-list-header :title="$t('CashFlow.Compilation.ExternalLOMS')" color="primary"></vs-list-header>
                    <draggable :list="filteredProjectLomList" group="loms" class="draggable-list cursor-move">
                        <vs-list-item v-for="(listItem, index) in filteredProjectLomList" :key="index" :title="listItem.name" :subtitle="listItem.description">
                          <vs-avatar slot="avatar" text="E" />
                          <!-- {{ listItem.projectName }} -->
                        </vs-list-item>
                    </draggable>
                  </vs-list>
                  <vs-list class="list-scrollable" v-show="!isExternalProject" >
                    <vs-list-header :title="$t('CashFlow.Compilation.HyperionLOMS')" color="primary"></vs-list-header>
                    <draggable :list="filteredHyperionLomList" group="loms" class="draggable-list cursor-move">
                        <vs-list-item v-for="(listItem, index) in filteredHyperionLomList" :key="index" :title="listItem.name" :subtitle="listItem.description">
                          <vs-avatar slot="avatar" :text="listItem.projectName" />
                        </vs-list-item>
                    </draggable>
                  </vs-list>
                </vs-col>
                <vs-col vs-w="3">
                  <vs-list>
                    <vs-list-header :title="$t('CashFlow.Compilation.IncludedLOMS')" color="primary"></vs-list-header>
                    <draggable :list="selectedLomList" group="loms" class="draggable-list cursor-move">
                        <vs-list-item v-for="(listItem, index) in selectedLomList" :key="index" :title="listItem.name" :subtitle="listItem.description">
                          <vs-avatar slot="avatar" :text="listItem.type" />
                        </vs-list-item>
                    </draggable>
                  </vs-list>
                </vs-col>
              </vs-row>
            </div>
          </vs-tab>
          <vs-tab value="2" :label="$t('CashFlow.Compilation.Tab3')" icon-pack="feather" icon="icon-settings">
            <div class="p-6 mb-10">
              <vs-row class="mb-3">
                <vs-col vs-w="4" class="mr-5">
                  <ValidationProvider :name="$t('CashFlow.Compilation.DiscountRate')" rules="required|decimal:2|between:0,100"  v-slot="{ errors }">
                    <validated-input
                      :label="$t('CashFlow.Compilation.DiscountRate')"
                      v-model="compilationDiscountRate"
                    ></validated-input>
                    <validation-message>{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>

              <vs-row class="mb-3">
                <vs-col vs-w="6" class="mr-5">
                  <form-label>{{$t('CashFlow.Compilation.ElementYearlyPriceTemplate')}}</form-label>
                  <vs-row>
                    <v-select
                      label="name"
                      :options="elementYearlyPriceTemplates"
                      v-model="compilationElementYearlyPriceTemplateId"
                      :reduce="template => template.id"
                      class="w-1/2"
                    />
                    <vs-button class="ml-3" @click="overrideElementPrices" v-if="!hasCustomizedElementPrices && this.compilationElementYearlyPriceTemplateId !== null && !this.selectedElementYearlyPriceTemplateChanged">{{ $t('CashFlow.Compilation.CustomizeRatesButton') }}</vs-button>
                    <vs-button class="ml-3" @click="cancelCustomElementPrices" v-if="hasCustomizedElementPrices && this.compilationElementYearlyPriceTemplateId !== null && !this.selectedElementYearlyPriceTemplateChanged">{{ $t('CashFlow.Compilation.CancelCustomizedRatesButton') }}</vs-button>
                  </vs-row>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3 yesscroll">
                <element-yearly-prices-table :yearlyPrices="elementYearlyPrices" :readOnly="!hasCustomizedElementPrices" v-if="this.compilationElementYearlyPriceTemplateId !== null && !this.selectedElementYearlyPriceTemplateChanged" />
              </vs-row>

              <vs-divider class="mb-3"></vs-divider>

              <vs-row class="mb-3">
                <vs-col vs-w="6" class="mr-5">
                  <form-label>{{$t('CashFlow.Compilation.YearlyInflationRateTemplate')}}</form-label>
                  <vs-row>
                    <v-select
                      label="name"
                      :options="yearlyInflationRateTemplates"
                      v-model="compilationYearlyInflationRateTemplateId"
                      :reduce="template => template.id"
                      class="w-1/2"
                    />
                    <vs-button class="ml-3" @click="overrideInflationRates" v-if="!hasCustomizedInflationRates && this.compilationYearlyInflationRateTemplateId !== null && !this.selectedInflationRateTemplateChanged">{{ $t('CashFlow.Compilation.CustomizeRatesButton') }}</vs-button>
                    <vs-button class="ml-3" @click="cancelCustomInflationRates" v-if="hasCustomizedInflationRates && this.compilationYearlyInflationRateTemplateId !== null && !this.selectedInflationRateTemplateChanged">{{ $t('CashFlow.Compilation.CancelCustomizedRatesButton') }}</vs-button>
                  </vs-row>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3 yesscroll">
                <yearly-inflation-rates-table :yearlyRates="yearlyInflationRates" :readOnly="!hasCustomizedInflationRates" v-if="this.compilationYearlyInflationRateTemplateId !== null && !this.selectedInflationRateTemplateChanged"/>                
              </vs-row>
              
              <vs-divider class="mb-3"></vs-divider>

              <vs-row class="mb-3">
                <vs-col vs-w="6" class="mr-5">
                  <form-label>{{$t('CashFlow.Compilation.YearlyFxRateTemplate')}}</form-label>
                  <vs-row>
                    <v-select
                      label="name"
                      :options="yearlyFxRateTemplates"
                      v-model="compilationYearlyFxRateTemplateId"
                      :reduce="template => template.id"
                    class="w-1/2"
                    />
                    <vs-button class="ml-3" @click="overrideFxRates" v-if="!hasCustomizedFxRates && this.compilationYearlyFxRateTemplateId !== null && !this.selectedFxRateTemplateChanged">{{ $t('CashFlow.Compilation.CustomizeRatesButton') }}</vs-button>
                    <vs-button class="ml-3" @click="cancelCustomFxRates" v-if="hasCustomizedFxRates && this.compilationYearlyFxRateTemplateId !== null && !this.selectedFxRateTemplateChanged">{{ $t('CashFlow.Compilation.CancelCustomizedRatesButton') }}</vs-button>
                  </vs-row>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3 yesscroll">
                <yearly-fx-rates-table :yearlyRates="yearlyFxRates" :readOnly="!hasCustomizedFxRates" v-if="this.compilationYearlyFxRateTemplateId !== null && !this.selectedFxRateTemplateChanged" />                
              </vs-row>
            </div>
          </vs-tab>
          <vs-tab value="3" :label="$t('CashFlow.Compilation.Tab4')" icon-pack="feather" icon="icon-user">
            <div class="p-6">
              <vs-row class="mb-3">
                <vs-col vs-w="4">
                  <vs-row class="mb-3">
                    <vs-col vs-w="12">
                      <ValidationProvider :name="$t('CashFlow.Compilation.UserEmail')" :rules="{ regex: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@agnicoeagle.com$/ }" v-slot="{ errors }">
                        <validated-input
                          :label="$t('CashFlow.Compilation.UserEmail')"
                          placeholder="john.doe@agnicoeagle.com"
                          v-model="compilationUser"
                          class="mb-4 md:mb-0 mr-4"
                        ></validated-input>
                        <validation-message>{{ errors[0] }}</validation-message>
                      </ValidationProvider>
                    </vs-col>
                  </vs-row>
                  <vs-row class="mb-3">
                    <vs-checkbox v-model="compilationCanEdit">{{$t('CanEdit')}}</vs-checkbox>
                  </vs-row>
                </vs-col>
                <vs-col vs-w="3">
                  <vs-button class="mt-10" type="border" icon-pack="feather" icon="icon-plus" color="primary" :disabled="compilationUser === ''" @click="addNewUser">{{$t('AddNew')}}</vs-button>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="12" class="mr-6">
                  <vs-table
                    ref="users"
                    v-model="selected"
                    :data="compilationUsers"
                    class="mb-3 mr-3"
                  >
                    <template slot="thead">
                      <vs-th sort-key="name">{{$t('CashFlow.Compilation.UserList')}}</vs-th>
                      <vs-th sort-key="canEdit">{{$t('CanEdit')}}</vs-th>
                      <vs-th>{{$t('Action')}}</vs-th>
                    </template>
                    <template slot-scope="{data}">
                      <tbody>
                        <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                          <vs-td>
                            <p class="font-medium truncate">{{ tr.userName }}</p>
                          </vs-td>
                          <vs-td width="75px;">
                            <vs-checkbox v-model="tr.canEdit" class="mb-2" :disabled="true" ></vs-checkbox>
                          </vs-td>
                          <vs-td width="100px" class="whitespace-no-wrap">
                            <feather-icon
                              icon="TrashIcon"
                              svgClasses="w-5 h-5 hover:text-danger stroke-current"
                              class="ml-2"
                              @click.stop="deleteUser(tr.userName)"
                            />
                          </vs-td>
                        </vs-tr>
                      </tbody>
                    </template>
                  </vs-table>
                </vs-col>
              </vs-row>
            </div>
          </vs-tab>
        </vs-tabs>
      </ValidationObserver>
    </vx-card>
  </div>
</template>

<script>

import router from '../../../router'
import moduleCashflow from "@/store/cashflow/moduleCashFlow.js";
import draggable from 'vuedraggable'
import vSelect from 'vue-select';
import YearlyFxRatesTable from './../templates/yearlyFxRateTemplates/components/yearlyFxRatesTable.vue'
import YearlyInflationRatesTable from './../templates/yearlyInflationRateTemplates/components/yearlyInflationRatesTable.vue'
import {
  FormLabel,
  ValidatedInput,
  ValidationMessage
} from "../../shared/components";
import ElementYearlyPricesTable from './../templates/elementYearlyPriceTemplates/components/ElementYearlyPricesTable.vue'

export default {
  props: {
    data: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    ValidatedInput,
    ValidationMessage,
    FormLabel,
    draggable,
    vSelect,
    YearlyFxRatesTable,
    YearlyInflationRatesTable,
    ElementYearlyPricesTable
  },
  data() {
    return {
      selected: [],
      compilationId: 0,
      compilationName: "",
      compilationDescription: "",
      compilationIsPublic: 0,
      compilationIsDecarbonizationScenario : 0,
      compilationCorporateLomId: null,
      compilationDiscountRate: 5,
      compilationYearlyFxRateTemplateId: null,
      originalFxRateTemplateId: null,
      compilationYearlyInflationRateTemplateId: null,
      originalInflationRateTemplateId: null,
      originalElementYearlyPriceTemplateId: null,
      compilationElementYearlyPriceTemplateId: null,
      compilationProjectLomId : [],
      compilationHyperionLoms : [],
      compilationUser : "",
      compilationCanEdit : false,
      compilationUsers : [],
      searchQuery: "",
      projectLomList : [],
      filteredProjectLomList : [],
      hyperionLomList : [],
      filteredHyperionLomList : [],
      selectedLomList: [],
      isExternalProject: true,
      selectedTab: 0,
      yearlyFxRates: [],
      hasCustomizedFxRates: false,
      yearlyInflationRates: [],
      hasCustomizedInflationRates: false,
      elementYearlyPrices: [],
      hasCustomizedElementPrices: false
    };
  },
  watch: {
    compilationYearlyFxRateTemplateId() {
      this.loadYearlyFxRates();
    },
    compilationYearlyInflationRateTemplateId() {
      this.loadYearlyInflationRates();
    },
    compilationElementYearlyPriceTemplateId() {
      this.loadElementYearlyPrices();
    }
  },
  computed: {
    projectLoms() {
      return this.$store.state.cashflow.projectLoms;
    },
    hyperionLoms() {
      return this.$store.state.cashflow.hyperionLoms;
    },
    elementYearlyPriceTemplates() {
      return this.$store.state.cashflow.elementYearlyPriceTemplates;
    },
    yearlyInflationRateTemplates() {
      return this.$store.state.cashflow.yearlyInflationRateTemplates;
    },
    yearlyFxRateTemplates() {
      return this.$store.state.cashflow.yearlyFxRateTemplates;
    },
    corporateLoms() {
      return this.$store.state.cashflow.corporateLoms;
    },
    selectedFxRateTemplateChanged() {
      return this.compilationYearlyFxRateTemplateId !== this.originalFxRateTemplateId;
    },
    selectedInflationRateTemplateChanged() {
      return this.compilationYearlyInflationRateTemplateId !== this.originalInflationRateTemplateId;
    },
    selectedElementYearlyPriceTemplateChanged() {
      return this.compilationElementYearlyPriceTemplateId !== this.originalElementYearlyPriceTemplateId;
    }
  },
  created() {
    if (!moduleCashflow.isRegistered) {
      this.$store.registerModule("cashflow", moduleCashflow);
      moduleCashflow.isRegistered = true;
    }
    
    if (this.$route.params.editData !== null) {
      this.$vs.loading();
      this.compilationId = this.$route.params.editData.id;
      this.refreshModel();
    } else {
      this.fillProjectLomList();
      this.fillHyperionLomList();
      this.loadYearlyFxRates();
      this.loadYearlyInflationRates();
      this.loadElementYearlyPrices();
    }
  },
  methods: {
    refreshModel() {
      this.$webApi.get("cashflow/compilation/" + this.compilationId).then((response) => {
        this.compilationName = response.data.name;
        this.compilationDescription = response.data.description;
        this.compilationIsPublic = response.data.isPublic;
        this.compilationIsDecarbonizationScenario = response.data.isDecarbonizationScenario;
        this.compilationCorporateLomId = response.data.corporateLomId;
        this.compilationProjectLomId = response.data.projectLomId;
        this.compilationHyperionLoms = response.data.hyperionLoms;
        this.compilationUsers = response.data.users;   
        this.compilationDiscountRate = response.data.discountRate;
        this.compilationElementYearlyPriceTemplateId = response.data.elementYearlyPriceTemplateId;
        this.originalElementYearlyPriceTemplateId = response.data.elementYearlyPriceTemplateId;
        this.compilationYearlyFxRateTemplateId = response.data.yearlyFxRateTemplateId;
        this.originalFxRateTemplateId = response.data.yearlyFxRateTemplateId;
        this.compilationYearlyInflationRateTemplateId = response.data.yearlyInflationRateTemplateId;
        this.originalInflationRateTemplateId = response.data.yearlyInflationRateTemplateId;
        this.hasCustomizedFxRates = response.data.hasCustomizedFxRates;
        this.hasCustomizedInflationRates = response.data.hasCustomizedInflationRates;
        this.hasCustomizedElementPrices = response.data.hasCustomizedElementYearlyPrice;
        this.selectedLomList = [];
        this.fillProjectLomList();
        this.fillHyperionLomList();
        this.loadYearlyFxRates();
        this.loadYearlyInflationRates();
        this.loadElementYearlyPrices();

      }).catch((error) => {
        this.$vs.notify({
          color: "danger",
          title: this.$t("LoadingError"),
          text: error.message  
        });
      }).finally(() => {
        this.$vs.loading.close();
      });
    },
    addNewUser() {
      this.$refs.observer.validate().then(result => {
        if (result) {
          if (this.compilationUser !== "" && this.compilationUsers.find(u => u.userName === this.compilationUser) === undefined) {
            const user = {
              userName : this.compilationUser,
              canEdit : this.compilationCanEdit
            };
            this.compilationUsers.unshift(user);
          }
        }
      });
    },
    deleteUser(userEmail) {
      const index = this.compilationUsers.findIndex((t) => t.userName === userEmail);
      if (index > -1) {
        this.compilationUsers.splice(index, 1);
      }
    },
    fillProjectLomList() {
      this.projectLomList = []
      this.$store.state.cashflow.projectLoms.forEach(p => {
        const lom = {
          id: p.id,
          scenarioId: null,
          versionId : null,
          entityId : null,
          projectName : p.projectName,
          name : p.projectName + " (" + p.name + ")",
          type : "External",
          description : p.description || ''
        }
        //Check if this project lom is already in the compilation
        if (this.compilationProjectLomId.find(p => p === lom.id) !== undefined) {
          this.selectedLomList.unshift(lom)
        }
        else {
          this.projectLomList.unshift(lom)
        }
      });

      this.filteredProjectLomList = this.projectLomList;
    },
    fillHyperionLomList() {
      this.hyperionLomList = []
      this.$store.state.cashflow.hyperionLoms.forEach(h => {
        const lom = {
          id: null,
          scenarioId: h.scenario,
          versionId : h.version,
          entityId : h.entityId,
          projectName : h.entityName,
          name : h.entityName + " (" + h.scenario + ")",
          type : "Hyperion",
          description : h.version || ''
        }
        //Check if this hyperion lom is already in the compilation
        if (this.compilationHyperionLoms.find(g => g.scenario === lom.scenarioId && g.entityId === lom.entityId && g.version === lom.versionId) !== undefined) {
          this.selectedLomList.unshift(lom)
        }
        else {
          this.hyperionLomList.unshift(lom)
        }
      });
      this.filteredHyperionLomList = this.hyperionLomList;
    },
    newData() {
      this.initValues();
      this.$refs.observer.reset();
    },
    updateSearchQuery(val) {
      this.filteredProjectLomList = this.projectLomList.filter(l => l.name.toUpperCase().includes(val.toUpperCase()) || l.description.toUpperCase().includes(val.toUpperCase()));
      this.filteredHyperionLomList = this.hyperionLomList.filter(l => l.name.toUpperCase().includes(val.toUpperCase()) || l.description.toUpperCase().includes(val.toUpperCase()));
    },
    editData() {
      const {id, name, description, isPublic, isDecarbonizationScenario, corporateLomId, projectLomId, hyperionLoms, discountRate, elementYearlyPriceTemplateId, yearlyFxRateTemplateId, yearlyInflationRateTemplateId, users, hasCustomizedFxRates, hasCustomizedInflationRates } = JSON.parse(JSON.stringify(this.data));
      this.compilationId = id;
      this.compilationName = name;
      this.compilationDescription = description;
      this.compilationIsPublic = isPublic;
      this.compilationIsDecarbonizationScenario = isDecarbonizationScenario;
      this.compilationCorporateLomId = corporateLomId;
      this.compilationProjectLomId = projectLomId;
      this.compilationHyperionLoms = hyperionLoms;
      this.compilationUsers = users;
      this.compilationDiscountRate = discountRate;
      this.compilationElementYearlyPriceTemplateId = elementYearlyPriceTemplateId;
      this.originalElementYearlyPriceTemplateId = elementYearlyPriceTemplateId;
      this.compilationYearlyFxRateTemplateId = yearlyFxRateTemplateId;
      this.originalFxRateTemplateId = yearlyFxRateTemplateId;
      this.compilationYearlyInflationRateTemplateId = yearlyInflationRateTemplateId;
      this.originalInflationRateTemplateId = yearlyInflationRateTemplateId;
      this.hasCustomizedFxRates = hasCustomizedFxRates;
      this.hasCustomizedInflationRates = hasCustomizedInflationRates;
      this.selectedTab = 0;
      this.$refs.observer.reset();
    },
    returnToList() {
      router.push("/bsg/cashflow/compilations");
    },
    initValues() {
      if (this.data.id) return;
      this.compilationId = 0;
      this.compilationName = "";
      this.compilationDescription = "";
      this.compilationIsPublic = true;
      this.compilationIsDecarbonizationScenario = false;
      this.compilationCorporateLomId = null;
      this.compilationDiscountRate = 5;
      this.compilationElementYearlyPriceTemplateId = null;
      this.originalElementYearlyPriceTemplateId = null;
      this.compilationYearlyFxRateTemplateId = null;
      this.originalFxRateTemplateId = null;
      this.compilationYearlyInflationRateTemplateId = null;
      this.originalInflationRateTemplateId = null;
      this.compilationIsDecarbonizationScenario = null;
      this.compilationProjectLomId = [];
      this.compilationHyperionLoms = [];
      this.compilationUser = "";
      this.compilationUsers = [];
      this.selectedLomList = [];
      this.selectedTab = 0;
      this.yearlyFxRates = [];
      this.hasCustomizedFxRates = false;
      this.yearlyInflationRates = [];
      this.hasCustomizedInflationRates = false;
    },
    submitData() {
      return this.$refs.observer.validate().then(result => {
        if (result) {
          this.$vs.loading();
          //Build projectLomId and hyperionLoms lists
          const projectList = [];
          const hyperionList = [];

          this.selectedLomList.forEach(lom => {
            if (lom.id !== null) {
              projectList.unshift(lom.id);
            }
            else {
              hyperionList.unshift({
                scenario: lom.scenarioId,
                entityId: lom.entityId,
                version: lom.versionId
              })
            }
          });

          const obj = {
            id: this.compilationId,
            name: this.compilationName,
            description: this.compilationDescription,
            corporateLomId: this.compilationCorporateLomId,
            discountRate: this.compilationDiscountRate,
            elementYearlyPriceTemplateId: this.compilationElementYearlyPriceTemplateId,
            yearlyFxRateTemplateId: this.compilationYearlyFxRateTemplateId,
            yearlyInflationRateTemplateId: this.compilationYearlyInflationRateTemplateId,
            isPublic: this.compilationIsPublic,
            isDecarbonizationScenario: this.compilationIsDecarbonizationScenario,
            projectLomId: projectList,
            hyperionLoms: hyperionList,
            users: this.compilationUsers
          };

          if (this.compilationId !== null && this.compilationId > 0) {
            this.$store.dispatch("cashflow/updateCompilation", obj).then(() => {
              this.$notifySaveSuccess();              
              this.refreshModel();
            });
          } else {
            this.$store.dispatch("cashflow/addCompilation", obj).then((response) => {
              this.$notifySaveSuccess();
              this.compilationId = response.data.id;
              this.refreshModel();
            });
          }
        }
      });
    },
    loadYearlyFxRates() {
      if (this.compilationId !== 0 && this.compilationYearlyFxRateTemplateId !== null) {
        this.$cqrsGet('cashflow/lom/compilations/findYearlyFxRates', { params: { id: this.compilationYearlyFxRateTemplateId, compilationId: this.compilationId}})
          .then((response) => {
            this.yearlyFxRates = response.data.model;
          }).finally(() => {
            this.$vs.loading.close();
          });
      }
    },
    overrideFxRates() {
      this.$cqrsPost('cashflow/lom/compilations/overrideFxRates', { id: this.compilationYearlyFxRateTemplateId, compilationId: this.compilationId })
        .then((response) => {
          this.$notifySaveSuccess();
          this.yearlyFxRates = response.data.model;
          this.hasCustomizedFxRates = true;
        }).finally(() => {
          this.$vs.loading.close();
        });
    },
    cancelCustomFxRates() {
      this.$cqrsPost('cashflow/lom/compilations/cancelCustomFxRates', { id: this.compilationYearlyFxRateTemplateId, compilationId: this.compilationId })
        .then((response) => {
          this.$notifySaveSuccess();
          this.yearlyFxRates = response.data.model;
          this.hasCustomizedFxRates = false;
        }).finally(() => {
          this.$vs.loading.close();
        });
    },
    loadYearlyInflationRates() {
      if (this.compilationId !== 0 && this.compilationYearlyInflationRateTemplateId !== null) {
        this.$cqrsGet('cashflow/lom/compilations/findYearlyInflationRates', { params: { id: this.compilationYearlyInflationRateTemplateId, compilationId: this.compilationId}})
          .then((response) => {
            this.yearlyInflationRates = response.data.model;
          }).finally(() => {
            this.$vs.loading.close();
          });
      }
    },
    overrideInflationRates() {
      this.$cqrsPost('cashflow/lom/compilations/overrideInflationRates', { id: this.compilationYearlyInflationRateTemplateId, compilationId: this.compilationId })
        .then((response) => {
          this.$notifySaveSuccess();
          this.yearlyInflationRates = response.data.model;
          this.hasCustomizedInflationRates = true;
        }).finally(() => {
          this.$vs.loading.close();
        });
    },
    cancelCustomInflationRates() {
      this.$cqrsPost('cashflow/lom/compilations/cancelCustomInflationRates', { id: this.compilationYearlyInflationRateTemplateId, compilationId: this.compilationId })
        .then((response) => {
          this.$notifySaveSuccess();
          this.yearlyInflationRates = response.data.model;
          this.hasCustomizedInflationRates = false;
        }).finally(() => {
          this.$vs.loading.close();
        });
    },
    loadElementYearlyPrices() {
      if (this.compilationId !== 0 && this.compilationElementYearlyPriceTemplateId !== null) {
        this.$cqrsGet('cashflow/lom/compilations/findElementYearlyPrices', { params: { id: this.compilationElementYearlyPriceTemplateId, compilationId: this.compilationId}})
          .then((response) => {
            this.elementYearlyPrices = response.data.model;
          }).finally(() => {
            this.$vs.loading.close();
          });
      }
    },
    overrideElementPrices() {
      this.$cqrsPost('cashflow/lom/compilations/overrideElementPrices', { id: this.compilationElementYearlyPriceTemplateId, compilationId: this.compilationId })
        .then((response) => {
          this.$notifySaveSuccess();
          this.elementYearlyPrices = response.data.model;
          this.hasCustomizedElementPrices = true;
        }).finally(() => {
          this.$vs.loading.close();
        });
    },
    cancelCustomElementPrices() {
      this.$cqrsPost('cashflow/lom/compilations/cancelCustomElementPrices', { id: this.compilationElementYearlyPriceTemplateId, compilationId: this.compilationId })
        .then((response) => {
          this.$notifySaveSuccess();
          this.elementYearlyPrices = response.data.model;
          this.hasCustomizedElementPrices = false;
        }).finally(() => {
          this.$vs.loading.close();
        });
    },
    canCalculate() {
      return this.compilationElementYearlyPriceTemplateId !== null 
        && this.compilationYearlyFxRateTemplateId !== null 
        && this.compilationYearlyInflationRateTemplateId !== null;
    },
    calculate() {
      return this.submitData().then(() => {
        this.$cqrsPost('cashflow/lom/compilations/calculate', { compilationId: this.compilationId, reportUrl: `/bsg/cashflow/reports/compilation?compilationId=${this.compilationId}` })
          .then((response) => {
            this.$vs.notify({
              title: this.$t("Success"),
              text: this.$t('JobsCenter.CalculationPending'),
              color: "success",
              iconPack: 'feather',
              icon:'icon-check'
            });
            this.returnToList();
          }).finally(() => {
            this.$vs.loading.close();
          });
      });
    }
  }
};
</script>
<style lang="scss">
  .list-scrollable {
    height:500px;/* or any height you want */
    overflow-y:auto;
  }
  .draggable-list {
    min-height: 300px !important;
  }
  .yesscroll {
    overflow-x: auto;
  }
</style>